import { useEffect, useState } from "react";
import { Printer, Mail, MessageSquare, Share2, Check, Link as LinkIcon } from "lucide-react";

export interface ShareReportProps {
  summary: string;
  shareUrl?: string;
}

export function ShareReport({ summary, shareUrl }: ShareReportProps) {
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  useEffect(() => {
    setMounted(true);
    setCanNativeShare(typeof navigator !== "undefined" && !!navigator.share);
  }, []);
  const url = shareUrl ?? (mounted ? window.location.href : "");
  const subject = "Coffman Cove Fishing Report";
  const body = `${summary}\n\nFull live report: ${url}`;

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: subject, text: summary, url });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(body);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        /* ignore */
      }
    }
  };

  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const sms = `sms:?&body=${encodeURIComponent(body)}`;

  return (
    <div className="no-print mt-5 flex flex-wrap items-center gap-2">
      <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/80 mr-1">
        Share report
      </span>
      <button
        type="button"
        onClick={handlePrint}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition px-3.5 py-2.5 min-h-11 text-xs font-medium text-white"
        aria-label="Save as PDF"
      >
        <Printer size={14} /> PDF
      </button>
      <a
        href={mailto}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition px-3.5 py-2.5 min-h-11 text-xs font-medium text-white"
        aria-label="Share via email"
      >
        <Mail size={14} /> Email
      </a>
      <a
        href={sms}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition px-3.5 py-2.5 min-h-11 text-xs font-medium text-white"
        aria-label="Share via text message"
      >
        <MessageSquare size={14} /> Text
      </a>
      <button
        type="button"
        onClick={handleNativeShare}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition px-3.5 py-2.5 min-h-11 text-xs font-medium text-white"
        aria-label="Share or copy link"
      >
        {copied ? <Check size={14} /> : canNativeShare ? <Share2 size={14} /> : <LinkIcon size={14} />}
        {copied ? "Copied" : "Share"}
      </button>
    </div>
  );
}
